import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import makeStyles from '@mui/styles/makeStyles';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';

import { PageTitle, PanelTitle, ProfileInfo } from 'ui';

import { EditProfileModal } from 'modules/common/Modals';

import { fetchUserImage } from 'services';
import { profilePicturesPath } from 'shared/constants/picturesPath';

const useStyles = makeStyles(theme => ({
  listItem: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  listItemPrimaryText: {
    width: 250,
  },
  listItemSecondaryTextHighlighted: {
    color: theme.palette.primary.main,
  },
}));

const Profile = () => {
  const { data } = useSelector(({ auth }) => auth);
  const { user } = data;

  const classes = useStyles();
  const { t } = useTranslation();
  const [editProfileModalOpen, setEditProfileModalOpen] = useState(false);
  const [fetchedAvatarUrl, setFetchedAvatarUrl] = useState(profilePicturesPath);

  const toggleEditProfileModal = () => setEditProfileModalOpen(!editProfileModalOpen);

  const fetchImage = async () => {
    try {
      const url = await fetchUserImage(data.userId);
      setFetchedAvatarUrl(url);
    } catch (e) {
      setFetchedAvatarUrl(profilePicturesPath);
    }
  };

  useEffect(() => {
    fetchImage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const avatarUrl =
    user && !!user.avatar ? `data:image/png;base64, ${user.avatar}` : fetchedAvatarUrl;

  const profileData = [
    {
      label: t('Telephone'),
      value: user && user.phone,
    },
    {
      label: t('E-mail address'),
      value: user && <span className={classes.listItemSecondaryTextHighlighted}>{user.email}</span>,
    },
    {
      label: t('Job title'),
      value: user && user.jobTitle,
    },
    {
      label: t('Job function'),
      value: user && user.jobFunction,
    },
    {
      label: t('Administration rights'),
      value: user && user.administrativeRights ? t('Yes') : t('No'),
    },
    {
      label: t('Platform module allocation'),
      value:
        user && user.licencesAndRoles.map(licenseOrRole => t(licenseOrRole.module.name)).join(', '),
    },
    {
      label: t('Credit Review permission'),
      value: user && user.hasCofaceLicense ? t('Yes') : t('No'),
    },
  ];

  return <>
    <Grid container spacing={2} justifyContent="space-between">
      <Grid item>
        <PageTitle>{t('Profile')}</PageTitle>
      </Grid>
      <Grid item>
        <Button variant="outlined" onClick={toggleEditProfileModal}>
          {t('Edit')}
        </Button>
      </Grid>
    </Grid>
    <ProfileInfo
      name={`${data.firstName} ${data.name}`}
      title={user && user.jobTitle}
      imageUrl={avatarUrl}
    />
    <PanelTitle>{t('Personal information')}</PanelTitle>
    <Paper>
      <List disablePadding>
        {profileData.map(({ label, value }, index) => (
          <ListItem
            key={label}
            className={classes.listItem}
            divider={profileData.length !== index + 1}
          >
            <Typography className={classes.listItemPrimaryText}>{label}</Typography>
            <Typography color="textSecondary">{value}</Typography>
          </ListItem>
        ))}
      </List>
    </Paper>
    <EditProfileModal open={editProfileModalOpen} onClose={toggleEditProfileModal} />
  </>;
};

export { Profile };
